import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { ArrowLeft, Save, Plus, Calendar, FileText, Plane, CreditCard, ScrollText, Hotel, Eye, Ship, Trash2, X, Download, Loader2, MessageCircle } from 'lucide-react';
import DayEditor from '@/components/ItineraryBuilder/DayEditor';
import TemplatesSection from '@/components/ItineraryBuilder/TemplatesSection';
import HotelEditor from '@/components/ItineraryBuilder/HotelEditor';
import VoucherTab from '@/components/ItineraryBuilder/VoucherTab';
import RichTextEditor from '@/components/RichTextEditor';
import ItineraryFlightCard from '@/components/itinerary/ItineraryFlightCard';
import { compressImageFile } from '@/lib/imageCompress';
import { useAuth } from '@/contexts/AuthContext';
import { calc_price } from '@/utils/pricing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// A1 — live "couple mode" preview for a pricing option so agents can sanity-check
// the chosen price basis (per_person / per_couple / total_package).
const fmtINRBuilder = (n) => '\u20B9' + Math.round(Number(n) || 0).toLocaleString('en-IN');
const optionCoupleTotal = (opt, discountPct) => {
  const raw = (opt.price != null && opt.price !== '')
    ? Number(opt.price)
    : Number(String((opt.items && opt.items[0] && opt.items[0].price) || '').replace(/[^0-9.]/g, '')) || 0;
  const r = calc_price(
    { pricingOptions: [{ price: raw, price_basis: opt.price_basis || 'per_person' }], discount_percentage: discountPct },
    'couple',
  );
  return r.total;
};

// Borderless input style — bottom border only, no box (per builder layout spec).
const BORDERLESS_INPUT =
  'w-full bg-transparent border-0 border-b border-slate-300 focus:border-primary-500 focus:outline-none px-0 py-1 text-slate-900';

// Subsection label divider — 10px uppercase tertiary, no box/border.
const PricingSubLabel = ({ children }) => (
  <div className="flex items-center gap-3 mt-8 mb-4">
    <span
      style={{ fontSize: '10px', letterSpacing: '0.08em', color: 'var(--color-text-tertiary, #94a3b8)' }}
      className="uppercase font-semibold whitespace-nowrap"
    >
      {children}
    </span>
    <span className="flex-1 h-px bg-slate-100" />
  </div>
);

// A3 — pax tabs the agent can expose to the client.
const PAX_TAB_OPTIONS = [
  { key: 'person', label: 'Per person' },
  { key: 'couple', label: 'Couple' },
  { key: 'family', label: 'Family' },
  { key: 'group', label: 'Group' },
];

const PaxTabsChecklist = ({ value, onToggle }) => (
  <div className="flex flex-wrap gap-5" data-testid="visible-pax-tabs">
    {PAX_TAB_OPTIONS.map((o) => (
      <label key={o.key} className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
        <input
          type="checkbox"
          checked={value.includes(o.key)}
          onChange={() => onToggle(o.key)}
          className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
          data-testid={`pax-tab-${o.key}`}
        />
        {o.label}
      </label>
    ))}
  </div>
);

// C4 — optional add-ons / supplement cost rows (borderless, bottom-border only).
const SUPP_BASIS = [
  { v: 'per_person', label: 'Per person' },
  { v: 'per_couple', label: 'Per couple' },
  { v: 'total', label: 'Total' },
];

const SupplementsEditor = ({ value, onChange }) => {
  const rows = Array.isArray(value) ? value : [];
  const update = (i, key, val) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const add = () => onChange([...rows, { title: '', description: '', price: 0, price_basis: 'per_person', is_optional: true, availability_note: '' }]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-7" data-testid="supplements-editor">
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end pb-2" data-testid={`supplement-edit-row-${i}`}>
          <div className="md:col-span-3">
            <label className="text-xs text-slate-500 mb-1 block">Title</label>
            <input value={r.title || ''} onChange={(e) => update(i, 'title', e.target.value)} className={BORDERLESS_INPUT + ' text-sm'} placeholder="Water Sports Package" data-testid={`supplement-title-${i}`} />
          </div>
          <div className="md:col-span-3">
            <label className="text-xs text-slate-500 mb-1 block">Description</label>
            <input value={r.description || ''} onChange={(e) => update(i, 'description', e.target.value)} className={BORDERLESS_INPUT + ' text-sm'} placeholder="Optional" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-slate-500 mb-1 block">Price (₹)</label>
            <input type="number" min="0" value={r.price ?? ''} onChange={(e) => update(i, 'price', e.target.value === '' ? 0 : (Number(e.target.value) || 0))} className={BORDERLESS_INPUT + ' text-sm'} placeholder="0" data-testid={`supplement-price-${i}`} />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-slate-500 mb-1 block">Basis</label>
            <select value={r.price_basis || 'per_person'} onChange={(e) => update(i, 'price_basis', e.target.value)} className={BORDERLESS_INPUT + ' text-sm'} data-testid={`supplement-basis-${i}`}>
              {SUPP_BASIS.map((b) => <option key={b.v} value={b.v}>{b.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-2 flex items-center justify-between gap-2">
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={r.is_optional !== false} onChange={(e) => update(i, 'is_optional', e.target.checked)} className="h-4 w-4 rounded border-slate-300" data-testid={`supplement-optional-${i}`} />
              Optional
            </label>
            <button type="button" onClick={() => remove(i)} className="text-red-400 hover:text-red-600 p-1" data-testid={`supplement-delete-${i}`} aria-label="Delete supplement">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          <div className="md:col-span-12">
            <label className="text-xs text-slate-500 mb-1 block">Availability note</label>
            <input value={r.availability_note || ''} onChange={(e) => update(i, 'availability_note', e.target.value)} className={BORDERLESS_INPUT + ' text-sm'} placeholder="e.g., Tuesday, Wednesday & Thursday only" data-testid={`supplement-availability-${i}`} />
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid="add-supplement-btn">
        <Plus className="h-4 w-4 mr-2" />Add supplement
      </Button>
    </div>
  );
};

// Correction 3 — sensible cruise inc/exc defaults (agent can edit/remove).
const DEFAULT_CRUISE_INCLUSIONS = [
  'All meals on board (breakfast, lunch, dinner)',
  'Onboard entertainment and activities',
  'Port charges and taxes',
  'Cabin steward service',
];
const DEFAULT_CRUISE_EXCLUSIONS = [
  'Shore excursions at ports',
  'Specialty dining restaurants',
  'Spa and salon services',
  'Gratuities / tips',
  'Travel insurance',
  'Beverages (alcoholic and non-alcoholic)',
];

const createEmptyDay = (dayNumber) => ({
  dayNumber,
  title: `Day ${dayNumber}`,
  description: '',
  imageUrl: ''
});

function ItineraryBuilder() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const linkedTripId = searchParams.get('tripId') || '';
  const templateId = searchParams.get('template') || '';
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(!!id || !!templateId);
  const [saving, setSaving] = useState(false);
  const [autoSavedAt, setAutoSavedAt] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [publicRef, setPublicRef] = useState('');     // ITI… ref for clean voucher URLs
  const [voucherBusy, setVoucherBusy] = useState(''); // '' | 'preview' | 'download' | 'whatsapp'
  const [clientContact, setClientContact] = useState({ phone: '', name: '' }); // linked traveller (TASK 3)
  
  // Form state
  const [title, setTitle] = useState('');
  const [tripId, setTripId] = useState(linkedTripId);
  const [destination, setDestination] = useState('');
  const [duration, setDuration] = useState(1);
  const [description, setDescription] = useState('');
  // Pax editor — Phase 12 bug fix. Surfaces adults/children/infants so the
  // agent can correct the value when the trip default (2/0/0) is wrong.
  // Saved verbatim to the itinerary doc; the backend `_resolve` helper
  // honours explicit 0s now (was treating 0 as "missing").
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [days, setDays] = useState([createEmptyDay(1)]);
  const [hotels, setHotels] = useState([]);
  const [inclusions, setInclusions] = useState([]);
  const [exclusions, setExclusions] = useState([]);
  // Destination templates (reusable per-destination Inclusions/Exclusions/T&C).
  const [destTemplates, setDestTemplates] = useState([]);
  const [dismissedTemplateId, setDismissedTemplateId] = useState(null);
  const [applyConfirm, setApplyConfirm] = useState(null);
  
  // PDF sections
  const [hotelDisclaimer, setHotelDisclaimer] = useState('');
  const [flightDetails, setFlightDetails] = useState('');
  // Flight section — structured-vs-manual toggle. `flightVouchers` is fetched
  // from /api/vouchers?trip_id= when a tripId is set. Mode is UI-only (no
  // DB persist); null means "use default" (structured if any voucher carries
  // segments[], else manual). Manual textarea content is preserved across
  // toggles — the toggle only flips which UI is rendered.
  const [flightVouchers, setFlightVouchers] = useState([]);
  const [selectedFlightVoucherId, setSelectedFlightVoucherId] = useState('');
  const [flightSectionMode, setFlightSectionMode] = useState(null); // null | 'structured' | 'manual'
  const [companyDoc, setCompanyDoc] = useState(null);
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [paymentPolicy, setPaymentPolicy] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentDetails, setPaymentDetails] = useState('');
  
  // Quote & Curator fields
  const [quoteBasis, setQuoteBasis] = useState('Per Person');
  const [pricingOptions, setPricingOptions] = useState([]);
  const [quoteNotes, setQuoteNotes] = useState([]);
  const [curatorName, setCuratorName] = useState('');
  const [curatorPhone, setCuratorPhone] = useState('');
  const [curatorEmail, setCuratorEmail] = useState('');
  const [roomDetails, setRoomDetails] = useState('');
  const [guestName, setGuestName] = useState('');
  const [packageType, setPackageType] = useState('custom');
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [heroCaption, setHeroCaption] = useState('');
  // BUG FIX + FEATURE — manual per-itinerary destination/city banner photo
  // override, keyed by normalized city text. Set from HotelEditor (per
  // hotel's city), takes priority over curated DESTINATION_IMAGES.
  const [destinationPhotoOverrides, setDestinationPhotoOverrides] = useState({});
  // Phase B2 FEATURE 2 — Trip Highlights (tag-input)
  const [highlights, setHighlights] = useState([]);
  const [highlightDraft, setHighlightDraft] = useState('');
  // Phase B2 FEATURE 4 — Optional payment link / amount surfaced on cover.
  const [paymentLink, setPaymentLink] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  // Correction 1 — agent-controlled discount (% + label), hidden by default.
  const [discountPercentage, setDiscountPercentage] = useState('');
  const [discountLabel, setDiscountLabel] = useState('');
  const [showDiscount, setShowDiscount] = useState(false);
  // Correction 3 — cruise inc/exc (shown only when a cruise stay exists).
  const [cruiseInclusions, setCruiseInclusions] = useState([]);
  const [cruiseExclusions, setCruiseExclusions] = useState([]);
  const cruiseSeededRef = useRef(false);
  // C4 — optional add-ons / supplement costs.
  const [supplementCosts, setSupplementCosts] = useState([]);
  // Pricing-engine meta set via API today — round-tripped so editing an
  // itinerary in the builder never silently wipes them.
  const [baseFlightPricePerPax, setBaseFlightPricePerPax] = useState(null);
  const [baseToursPricePerPax, setBaseToursPricePerPax] = useState(null);
  const [advancePercentage, setAdvancePercentage] = useState(null);
  // 1b — recommended_option/recommendation_reason retired (no 2nd option to recommend between).
  // A2 — international tour → TCS applies.
  const [isInternational, setIsInternational] = useState(true);
  // GST (agent-internal): none / package(5%) / service_fee(18%) + collapsed section.
  const [gstModel, setGstModel] = useState('none');
  const [serviceFee, setServiceFee] = useState('');
  // A3 — visible pax tabs. 1b — price-breakup toggle retired (client
  // preview never shows an itemised breakdown regardless — lump-sum only).
  const [visiblePaxModes, setVisiblePaxModes] = useState(['couple']);
  const [showFamilyGroupTabs, setShowFamilyGroupTabs] = useState(false);

  // Task 2 — Voucher data model
  const [voucherStatus, setVoucherStatus] = useState(null);
  const [bookingRef, setBookingRef] = useState('');
  const [coordinator, setCoordinator] = useState(null);
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [thingsToCarry, setThingsToCarry] = useState([]);
  const [transportOptions, setTransportOptions] = useState([]);
  const [recommendedPlaces, setRecommendedPlaces] = useState([]);
  const [localCurrency, setLocalCurrency] = useState(null);
  const [voucherNotes, setVoucherNotes] = useState('');

  // A3 — toggle a pax tab on/off; never allow an empty set (fall back to couple).
  const togglePaxMode = (key) => {
    setVisiblePaxModes((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      return next.length > 0 ? next : ['couple'];
    });
  };

  const fetchItinerary = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/itineraries/${id}`);
      const data = res.data;
      setTitle(data.title || '');
      setTripId(data.tripId || '');
      setPublicRef(data.public_ref || '');
      // TASK 3 — pull the linked traveller's WhatsApp number for "Send Voucher".
      if (data.tripId) {
        axios.get(`${BACKEND_URL}/api/trips/${data.tripId}`)
          .then((tr) => setClientContact({ phone: tr.data?.phone || '', name: tr.data?.guestName || '' }))
          .catch(() => {});
      }
      setDestination(data.destination || '');
      setDuration(data.duration || 1);
      setDescription(data.description || '');
      setAdults(data.adults != null ? Number(data.adults) : 2);
      setChildren(data.children != null ? Number(data.children) : 0);
      setInfants(data.infants != null ? Number(data.infants) : 0);
      setDays(data.days?.length ? data.days : [createEmptyDay(1)]);
      setHotels(data.hotels || []);
      setInclusions(data.inclusions || []);
      setExclusions(data.exclusions || []);
      setHotelDisclaimer(data.hotelDisclaimer || '');
      setFlightDetails(data.flightDetails || '');
      setTermsAndConditions(data.termsAndConditions || '');
      setPaymentPolicy(data.paymentPolicy || '');
      setNotes(data.notes || '');
      setPaymentDetails(data.paymentDetails || '');
      setQuoteBasis(data.quoteBasis || 'Per Person');
      setPricingOptions((data.pricingOptions || []).slice(0, 1));
      setQuoteNotes(data.quoteNotes || []);
      setCuratorName(data.curatorName || '');
      setCuratorPhone(data.curatorPhone || '');
      setCuratorEmail(data.curatorEmail || '');
      setRoomDetails(data.roomDetails || '');
      setGuestName(data.guestName || '');
      setPackageType(data.packageType || 'custom');
      setHeroImageUrl(data.heroImageUrl || '');
      setHeroCaption(data.heroCaption || '');
      setDestinationPhotoOverrides(data.destinationPhotoOverrides && typeof data.destinationPhotoOverrides === 'object' ? data.destinationPhotoOverrides : {});
      setHighlights(Array.isArray(data.highlights) ? data.highlights : []);
      setPaymentLink(data.paymentLink || '');
      setPaymentAmount(
        data.paymentAmount === 0 || data.paymentAmount == null
          ? ''
          : String(data.paymentAmount),
      );
      // Correction 1 — discount
      setDiscountPercentage(data.discount_percentage != null ? String(data.discount_percentage) : '');
      setDiscountLabel(data.discount_label || '');
      setShowDiscount(data.discount_percentage != null && data.discount_percentage !== '');
      // Correction 3 — cruise inc/exc (mark seeded so we don't overwrite saved values)
      setCruiseInclusions(Array.isArray(data.cruise_inclusions) ? data.cruise_inclusions : []);
      setCruiseExclusions(Array.isArray(data.cruise_exclusions) ? data.cruise_exclusions : []);
      setSupplementCosts(Array.isArray(data.supplement_costs) ? data.supplement_costs : []);
      if ((data.cruise_inclusions || []).length > 0 || (data.cruise_exclusions || []).length > 0) {
        cruiseSeededRef.current = true;
      }
      // Pricing-engine meta — preserved verbatim across builder saves.
      setBaseFlightPricePerPax(data.base_flight_price_per_pax ?? null);
      setBaseToursPricePerPax(data.base_tours_price_per_pax ?? null);
      setAdvancePercentage(data.advance_percentage ?? null);
      // A2 — international/TCS (default international when unset)
      setIsInternational(data.is_international !== false);
      // GST (agent-internal)
      setGstModel(data.gst_model || 'none');
      setServiceFee(data.service_fee != null ? String(data.service_fee) : '');
      // A3 — visible pax tabs
      setVisiblePaxModes(Array.isArray(data.visible_pax_modes) && data.visible_pax_modes.length > 0 ? data.visible_pax_modes : ['couple']);
      setShowFamilyGroupTabs(!!data.show_family_group_tabs);
      // Task 2 — Voucher data
      setVoucherStatus(data.voucher_status ?? null);
      setBookingRef(data.booking_ref || '');
      setCoordinator(data.coordinator || null);
      setEmergencyContacts(Array.isArray(data.emergency_contacts) ? data.emergency_contacts : []);
      setThingsToCarry(Array.isArray(data.things_to_carry) ? data.things_to_carry : []);
      setTransportOptions(Array.isArray(data.transport_options) ? data.transport_options : []);
      setRecommendedPlaces(Array.isArray(data.recommended_places) ? data.recommended_places : []);
      setLocalCurrency(data.local_currency || null);
      setVoucherNotes(data.voucher_notes || '');
    } catch (err) {
      toast.error('Failed to load itinerary');
      navigate('/itineraries');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/templates/inclusions-exclusions`);
      setTemplates(res.data);
      
      if (res.data.length === 0) {
        await axios.post(`${BACKEND_URL}/api/templates/seed-defaults`);
        const seededRes = await axios.get(`${BACKEND_URL}/api/templates/inclusions-exclusions`);
        setTemplates(seededRes.data);
      }
    } catch (err) {
      console.debug('Templates fetch skipped (will use seed defaults):', err?.message || err);
    }
  }, []);

  const fetchDestTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/templates/destinations`);
      setDestTemplates(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.debug('Destination templates fetch skipped:', err?.message || err);
    }
  }, []);

  // Fetch settings for default T&C pre-fill (new itineraries only)
  const fetchSettingsDefaults = useCallback(async () => {
    if (id || templateId) return; // Only for brand new itineraries (not editing or from template)
    try {
      const res = await axios.get(`${BACKEND_URL}/api/settings`);
      const settings = res.data;
      if (settings.defaultTermsAndConditions && !termsAndConditions) {
        setTermsAndConditions(settings.defaultTermsAndConditions);
      }
      if (settings.defaultPaymentPolicy && !paymentPolicy) {
        setPaymentPolicy(settings.defaultPaymentPolicy);
      }
    } catch (err) {
      console.debug('Default T&C fetch skipped (non-critical):', err?.message || err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, templateId]);

  // Auto-fill curator from logged-in user (new itineraries only)
  useEffect(() => {
    if (!id && !templateId && user && !curatorName) {
      setCuratorName(user.name || '');
      setCuratorEmail(user.email || '');
      setCuratorPhone(user.phone || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id, templateId]);


  // Load template data for "Quick Duplicate & Customize"
  const fetchTemplateData = useCallback(async () => {
    if (!templateId) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/itineraries/${templateId}`);
      const data = res.data;
      setTitle(`${data.title || ''} - Custom`);
      setTripId('');
      setDestination(data.destination || '');
      setDuration(data.duration || 1);
      setDescription(data.description || '');
      setDays(data.days?.length ? data.days : [createEmptyDay(1)]);
      setHotels(data.hotels || []);
      setInclusions(data.inclusions || []);
      setExclusions(data.exclusions || []);
      setHotelDisclaimer(data.hotelDisclaimer || '');
      setFlightDetails(data.flightDetails || '');
      setTermsAndConditions(data.termsAndConditions || '');
      setPaymentPolicy(data.paymentPolicy || '');
      setNotes('');
      setPaymentDetails(data.paymentDetails || '');
      setQuoteBasis(data.quoteBasis || 'Per Person');
      setPricingOptions((data.pricingOptions || []).slice(0, 1));
      setQuoteNotes(data.quoteNotes || []);
      setCuratorName(data.curatorName || '');
      setCuratorPhone(data.curatorPhone || '');
      setCuratorEmail(data.curatorEmail || '');
      setRoomDetails(data.roomDetails || '');
      setGuestName('');
      setPackageType('custom');
      setHeroImageUrl(data.heroImageUrl || '');
      setHeroCaption(data.heroCaption || '');
      setDestinationPhotoOverrides(data.destinationPhotoOverrides && typeof data.destinationPhotoOverrides === 'object' ? data.destinationPhotoOverrides : {});
      setHighlights(Array.isArray(data.highlights) ? data.highlights : []);
      toast.success('Template loaded! Customize and save as new itinerary.');
    } catch (err) {
      toast.error('Failed to load template');
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    fetchTemplates();
    fetchDestTemplates();
    fetchSettingsDefaults();
    if (id) fetchItinerary();
    else if (templateId) fetchTemplateData();
  }, [id, templateId, fetchItinerary, fetchTemplates, fetchDestTemplates, fetchSettingsDefaults, fetchTemplateData]);

  // Correction 3 — does any accommodation in this itinerary sail?
  const hasCruise = (hotels || []).some((h) => (h?.accommodation_type || 'hotel') === 'cruise');
  // Pre-fill cruise inc/exc the first time a cruise stay appears (agent edits/removes).
  useEffect(() => {
    if (hasCruise && !cruiseSeededRef.current && cruiseInclusions.length === 0 && cruiseExclusions.length === 0) {
      setCruiseInclusions(DEFAULT_CRUISE_INCLUSIONS);
      setCruiseExclusions(DEFAULT_CRUISE_EXCLUSIONS);
      cruiseSeededRef.current = true;
    }
  }, [hasCruise, cruiseInclusions.length, cruiseExclusions.length]);

  // ---- Destination template apply (manual dropdown + auto-match chip) ----
  const matchDestTemplate = (name, list) => {
    const needle = (name || '').trim().toLowerCase();
    if (!needle) return null;
    let best = null;
    let bestScore = -1;
    for (const t of list) {
      const tn = (t.destination_name || '').trim().toLowerCase();
      if (!tn || tn === 'global default') continue;
      if (tn.includes(needle) || needle.includes(tn)) {
        const score = tn.length + (t.is_default ? 1000 : 0);
        if (score > bestScore) { bestScore = score; best = t; }
      }
    }
    return best;
  };

  const doApplyTemplate = (t) => {
    if (!t) return;
    setInclusions(Array.isArray(t.inclusions) ? [...t.inclusions] : []);
    setExclusions(Array.isArray(t.exclusions) ? [...t.exclusions] : []);
    if (t.terms_and_conditions) setTermsAndConditions(t.terms_and_conditions);
    setDismissedTemplateId(t.id);
    toast.success(`Applied "${t.destination_name}" template`);
  };

  const requestApplyTemplate = (t) => {
    if (!t) return;
    const hasContent = inclusions.length > 0 || exclusions.length > 0
      || (termsAndConditions && termsAndConditions !== '<p></p>');
    if (hasContent) setApplyConfirm(t);
    else doApplyTemplate(t);
  };

  const matchedTemplate = matchDestTemplate(destination, destTemplates);
  const showDestChip = Boolean(matchedTemplate) && matchedTemplate.id !== dismissedTemplateId;
  const destTemplateOptions = destTemplates.map((t) => (
    <SelectItem key={t.id} value={t.id} data-testid={`apply-dest-option-${t.id}`}>
      {t.destination_name}
    </SelectItem>
  ));

  // Fetch flight vouchers for the linked trip so the Flight Details section
  // can offer a "structured view" mode. Re-runs when tripId changes (manual
  // edit or itinerary load). When tripId is empty we clear the list so the
  // section defaults back to manual entry.
  useEffect(() => {
    if (!tripId) {
      setFlightVouchers([]);
      setSelectedFlightVoucherId('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/vouchers`, { params: { trip_id: tripId } });
        if (cancelled) return;
        const flights = Array.isArray(res.data)
          ? res.data.filter((v) => v.type === 'flight')
          : [];
        setFlightVouchers(flights);
        // Auto-select when exactly one voucher exists; clear otherwise so the
        // dropdown prompts the agent to choose explicitly.
        setSelectedFlightVoucherId(flights.length === 1 ? flights[0].voucherId : '');
      } catch (err) {
        if (!cancelled) {
          setFlightVouchers([]);
          setSelectedFlightVoucherId('');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [tripId]);

  // Brand tokens for ItineraryFlightCard. One-shot fetch — non-critical, the
  // card falls back to brand defaults when companyDoc is null.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/public/company`)
      .then((r) => { if (!cancelled) setCompanyDoc(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleDurationChange = (newDuration) => {
    // Task 8 — let the field be cleared and retyped freely; never snap to 1
    // mid-typing, never allow negatives. Days only (re)generate for a valid
    // positive integer (1–30).
    const raw = String(newDuration);
    if (raw === '') { setDuration(''); return; }
    let n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return;
    n = Math.max(1, Math.min(30, n));
    setDuration(n);

    const newDays = [];
    for (let i = 1; i <= n; i++) {
      const existingDay = days.find(d => d.dayNumber === i);
      newDays.push(existingDay || createEmptyDay(i));
    }
    setDays(newDays);
  };

  const handleDurationBlur = () => {
    // On blur, restore a sane value if the field was left empty/invalid.
    const n = parseInt(duration, 10);
    if (duration === '' || !Number.isFinite(n) || n < 1) {
      handleDurationChange(days.length || 1);
    }
  };

  const updateDay = (index, field, value) => {
    setDays(prev => prev.map((d, i) => i === index ? { ...d, [field]: value } : d));
  };

  const handleHotelImageUpload = async (hotelIndex, rawFile) => {
    try {
      const file = await compressImageFile(rawFile, { maxSizeMB: 0.8, maxWidthOrHeight: 1600 });
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const uploadRes = await axios.post(
        `${BACKEND_URL}/api/itineraries/${id || 'temp'}/upload-image`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setHotels(prev => prev.map((h, i) => i === hotelIndex ? { ...h, imageUrl: uploadRes.data.imageUrl } : h));
      toast.success('Photo uploaded!');
    } catch (err) {
      toast.error('Failed to upload photo');
    }
  };

  // FEATURE — manual per-itinerary destination/city banner photo override.
  // Reuses the same generic upload-image endpoint as hotel/day photos (no
  // DB write on upload); the URL lands in local state and is persisted with
  // the rest of the itinerary on Save.
  const handleDestinationPhotoUpload = async (cityKey, rawFile) => {
    if (!cityKey) { toast.error('Enter a city for this hotel first'); return; }
    try {
      const file = await compressImageFile(rawFile, { maxSizeMB: 0.8, maxWidthOrHeight: 1600 });
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const uploadRes = await axios.post(
        `${BACKEND_URL}/api/itineraries/${id || 'temp'}/upload-image`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setDestinationPhotoOverrides((prev) => ({ ...prev, [cityKey]: uploadRes.data.imageUrl }));
      toast.success(`Banner photo set for ${cityKey}!`);
    } catch (err) {
      toast.error('Failed to upload banner photo');
    }
  };

  const handleDestinationPhotoClear = (cityKey) => {
    setDestinationPhotoOverrides((prev) => {
      const next = { ...prev };
      delete next[cityKey];
      return next;
    });
  };

  const removeDay = (index) => {
    setDays(prev => {
      const filtered = prev.filter((_, i) => i !== index);
      return filtered.map((d, i) => ({ ...d, dayNumber: i + 1 }));
    });
    setDuration(prev => Math.max(1, prev - 1));
  };

  const handleImageUpload = async (dayIndex, rawFile) => {
    try {
      const file = await compressImageFile(rawFile, { maxSizeMB: 0.8, maxWidthOrHeight: 1600 });
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const uploadRes = await axios.post(
        `${BACKEND_URL}/api/itineraries/${id || 'temp'}/upload-image`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      updateDay(dayIndex, 'imageUrl', uploadRes.data.imageUrl);
      const saved = file.size < rawFile.size
        ? `Image uploaded (compressed ${Math.round((1 - file.size / rawFile.size) * 100)}%)`
        : 'Image uploaded!';
      toast.success(saved);
    } catch (err) {
      toast.error('Failed to upload image');
    }
  };

  const toggleInclusion = (text) => {
    setInclusions(prev => 
      prev.includes(text) ? prev.filter(i => i !== text) : [...prev, text]
    );
  };

  const toggleExclusion = (text) => {
    setExclusions(prev => 
      prev.includes(text) ? prev.filter(e => e !== text) : [...prev, text]
    );
  };

  const addTemplate = async (text, category) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/templates/inclusions-exclusions`, { text, category });
      setTemplates(prev => [...prev, res.data]);
      if (category === 'inclusion') {
        setInclusions(prev => [...prev, text]);
      } else {
        setExclusions(prev => [...prev, text]);
      }
      toast.success('Template added!');
    } catch (err) {
      toast.error('Failed to add template');
    }
  };

  const editTemplate = async (templateId, newText) => {
    try {
      await axios.put(`${BACKEND_URL}/api/templates/inclusions-exclusions/${templateId}`, { text: newText });
      setTemplates(prev => prev.map(t => t.id === templateId ? { ...t, text: newText } : t));
      toast.success('Template updated!');
    } catch (err) {
      toast.error('Failed to update template');
    }
  };

  const deleteTemplate = async (templateId) => {
    try {
      const template = templates.find(t => t.id === templateId);
      await axios.delete(`${BACKEND_URL}/api/templates/inclusions-exclusions/${templateId}`);
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      // Also remove from selected inclusions/exclusions
      if (template) {
        setInclusions(prev => prev.filter(i => i !== template.text));
        setExclusions(prev => prev.filter(e => e !== template.text));
      }
      toast.success('Template deleted!');
    } catch (err) {
      toast.error('Failed to delete template');
    }
  };

  // Build the full itinerary payload from current state — shared by manual
  // save (handleSubmit) and the 60s auto-save.
  const buildItineraryData = () => {
    return {
      title,
      tripId,
      destination,
      duration: parseInt(duration) || days.length || 1,
      description,
      // Pax counts — send as numbers; 0 is legitimate.
      adults: Number(adults) || 0,
      children: Number(children) || 0,
      infants: Number(infants) || 0,
      days,
      hotels,
      inclusions,
      exclusions,
      hotelDisclaimer,
      flightDetails,
      termsAndConditions,
      paymentPolicy,
      notes,
      paymentDetails,
      quoteBasis,
      pricingOptions,
      quoteNotes,
      curatorName,
      curatorPhone,
      curatorEmail,
      roomDetails,
      guestName,
      packageType,
      heroImageUrl,
      heroCaption,
      destinationPhotoOverrides,
      highlights: Array.isArray(highlights) ? highlights : [],
      paymentLink: (paymentLink || '').trim(),
      paymentAmount: paymentAmount === '' ? 0 : Number(paymentAmount) || 0,
      // Correction 1 — discount
      discount_percentage: (showDiscount && discountPercentage !== '') ? (Number(discountPercentage) || 0) : null,
      discount_label: (showDiscount && discountLabel.trim()) ? discountLabel.trim() : null,
      // Correction 3 — cruise inc/exc
      cruise_inclusions: Array.isArray(cruiseInclusions) ? cruiseInclusions : [],
      cruise_exclusions: Array.isArray(cruiseExclusions) ? cruiseExclusions : [],
      supplement_costs: Array.isArray(supplementCosts) ? supplementCosts.filter((s) => s && (s.title || '').trim()) : [],
      // Pricing-engine meta — preserved verbatim (set via API today).
      base_flight_price_per_pax: baseFlightPricePerPax,
      base_tours_price_per_pax: baseToursPricePerPax,
      advance_percentage: advancePercentage != null ? advancePercentage : 0.25,
      recommended_option: null,
      recommendation_reason: null,
      // A2 — TCS international gate
      is_international: isInternational,
      apply_tcs: isInternational,
      // GST (agent-internal)
      gst_model: gstModel || 'none',
      service_fee: serviceFee === '' ? null : (Number(serviceFee) || 0),
      // A3 — visible pax tabs. 1b — show_price_breakup retired (always false;
      // self-cleans on next save, the client preview never shows a breakdown).
      visible_pax_modes: Array.isArray(visiblePaxModes) && visiblePaxModes.length > 0 ? visiblePaxModes : ['couple'],
      show_price_breakup: false,
      show_family_group_tabs: showFamilyGroupTabs,
      // Task 2 — Voucher data model
      voucher_status: voucherStatus,
      booking_ref: bookingRef || null,
      coordinator,
      emergency_contacts: Array.isArray(emergencyContacts) ? emergencyContacts : [],
      things_to_carry: Array.isArray(thingsToCarry) ? thingsToCarry : [],
      transport_options: Array.isArray(transportOptions) ? transportOptions : [],
      recommended_places: Array.isArray(recommendedPlaces) ? recommendedPlaces : [],
      local_currency: localCurrency,
      voucher_notes: voucherNotes || null
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const data = buildItineraryData();
    try {
      if (id) {
        await axios.put(`${BACKEND_URL}/api/itineraries/${id}`, data);
        toast.success('Itinerary updated!');
      } else {
        const res = await axios.post(`${BACKEND_URL}/api/itineraries`, data);
        toast.success('Itinerary created!');
        // Navigate to edit mode with the new ID
        navigate(`/itineraries/${res.data.id}`);
        return;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save itinerary');
    } finally {
      setSaving(false);
    }
  };

  // Auto-save: silently PUT the current payload every 60s while editing an
  // existing itinerary (covers the itinerary AND its Voucher section, which
  // share this one form). New/unsaved drafts are skipped until first save.
  const autoSaveRef = useRef(null);
  autoSaveRef.current = async () => {
    if (!id || saving) return;
    try {
      await axios.put(`${BACKEND_URL}/api/itineraries/${id}`, buildItineraryData());
      setAutoSavedAt(new Date());
    } catch (_) { /* silent — manual save still surfaces errors */ }
  };
  useEffect(() => {
    const t = setInterval(() => { if (autoSaveRef.current) autoSaveRef.current(); }, 60000);
    return () => clearInterval(t);
  }, []);

  const handlePreview = () => {
    if (!id) {
      toast.info('Please save the itinerary first to preview');
      return;
    }
    navigate(`/itineraries/${id}/preview`);
  };

  // Persist the Section-10 voucher edits, then resolve the clean ITI… ref the
  // public voucher page + PDF endpoint key on. Returns '' when it can't.
  const saveAndGetVoucherRef = async () => {
    if (!id) {
      toast.info('Please save the itinerary first');
      return '';
    }
    try {
      const res = await axios.put(`${BACKEND_URL}/api/itineraries/${id}`, buildItineraryData());
      const ref = res?.data?.public_ref || publicRef || id;
      if (res?.data?.public_ref) setPublicRef(res.data.public_ref);
      setAutoSavedAt(new Date());
      return ref || '';
    } catch (err) {
      toast.error('Could not save the voucher before opening it');
      return '';
    }
  };

  // Open the client-facing itinerary voucher (same document that downloads as
  // PDF) — this IS the trip's main itinerary voucher, built from Section 10.
  const handlePreviewVoucher = async () => {
    setVoucherBusy('preview');
    try {
      const ref = await saveAndGetVoucherRef();
      if (ref) window.open(`/${ref}/voucher/itinerary`, '_blank', 'noopener');
    } finally {
      setVoucherBusy('');
    }
  };

  // Download the Section-10 itinerary voucher as a branded PDF.
  const handleDownloadVoucher = async () => {
    setVoucherBusy('download');
    try {
      const ref = await saveAndGetVoucherRef();
      if (!ref) return;
      const res = await axios.get(
        `${BACKEND_URL}/api/trip/${encodeURIComponent(ref)}/voucher/pdf?section=itinerary`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `travcations_${ref}_itinerary_voucher.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Itinerary voucher downloaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Voucher PDF generation failed');
    } finally {
      setVoucherBusy('');
    }
  };

  // TASK 3 — one-tap "Send Voucher on WhatsApp": saves Section-10, then opens a
  // wa.me deep-link to the linked traveller with the public voucher URL.
  const handleSendVoucherWhatsApp = async () => {
    setVoucherBusy('whatsapp');
    try {
      const ref = await saveAndGetVoucherRef();
      if (!ref) return;
      const voucherUrl = `${window.location.origin}/${ref}/voucher`;
      // Respect an embedded country code; default a bare 10-digit to +91.
      let num = String(clientContact.phone || '').replace(/[^0-9]/g, '').replace(/^0+/, '');
      if (num.length === 10) num = `91${num}`;
      const hi = clientContact.name ? `Hi ${clientContact.name}, ` : 'Hi, ';
      const text = `${hi}here is your Travcations travel voucher${title ? ` for ${title}` : ''}.\n\nView & download it here:\n${voucherUrl}`;
      const link = `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
      if (!num) toast.info('No traveller phone on the linked trip — pick a contact in WhatsApp');
      window.open(link, '_blank', 'noopener');
    } finally {
      setVoucherBusy('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-700"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-12 overflow-x-hidden">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" />Back
        </Button>
        {id && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              type="button"
              onClick={async () => {
                if (!window.confirm('Generate the combined itinerary voucher for this trip? It includes every day in one document and replaces any earlier auto-generated voucher.')) return;
                try {
                  await axios.post(`${BACKEND_URL}/api/vouchers/rebrand/from-itinerary/${id}`);
                  toast.success('Combined itinerary voucher created');
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Generation failed');
                }
              }}
              className="rounded-xl"
              data-testid="generate-day-vouchers-btn"
            >
              <FileText className="h-4 w-4 mr-2" />Itinerary Voucher
            </Button>
            <Button variant="outline" onClick={handlePreview} className="rounded-xl">
              <Eye className="h-4 w-4 mr-2" />Preview Itinerary
            </Button>
          </div>
        )}
      </div>

      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/5 to-accent-600/5 rounded-3xl blur-3xl"></div>
        <div className="relative">
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-slate-900 tracking-tight mb-3">
            {id ? 'Edit Itinerary' : 'Create Itinerary'}
          </h1>
          <p className="text-base lg:text-lg text-slate-600 font-light">Design a complete travel experience</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <Card className="p-6 sm:p-8 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-6 flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary-700" /> Basic Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Itinerary Title *</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} required className="h-12 rounded-xl" placeholder="e.g., Magical Maldives 5N/6D" data-testid="input-title" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Destination *</Label>
              <Input value={destination} onChange={(e) => setDestination(e.target.value)} required className="h-12 rounded-xl" placeholder="e.g., Maldives" data-testid="input-destination" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Number of Days *</Label>
              <Input type="number" min="1" max="30" value={duration} onChange={(e) => handleDurationChange(e.target.value)} onBlur={handleDurationBlur} onKeyDown={(e) => { if (e.key === '-' || e.key === 'e') e.preventDefault(); }} required className="h-12 rounded-xl" data-testid="input-duration" />
              <p className="text-xs text-slate-500 mt-1">Days auto-generate based on this</p>
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Link to Trip (Optional)</Label>
              <Input value={tripId} onChange={(e) => setTripId(e.target.value)} className="h-12 rounded-xl" placeholder="e.g., TRV_TRIP_0226_0001" />
            </div>
            {/* Pax editor — Phase 12. The itinerary stores its own copy of
                adults/children/infants. Edit here so kids-only or solo
                trips render correctly in the preview. */}
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Adults</Label>
              <Input
                type="number"
                min="0"
                max="40"
                value={adults}
                onChange={(e) => setAdults(parseInt(e.target.value, 10) || 0)}
                className="h-12 rounded-xl"
                data-testid="input-adults"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Children</Label>
              <Input
                type="number"
                min="0"
                max="40"
                value={children}
                onChange={(e) => setChildren(parseInt(e.target.value, 10) || 0)}
                className="h-12 rounded-xl"
                data-testid="input-children"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Infants</Label>
              <Input
                type="number"
                min="0"
                max="40"
                value={infants}
                onChange={(e) => setInfants(parseInt(e.target.value, 10) || 0)}
                className="h-12 rounded-xl"
                data-testid="input-infants"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Overview</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="rounded-xl" placeholder="Brief overview" data-testid="input-description" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Package Type</Label>
              <select value={packageType} onChange={(e) => setPackageType(e.target.value)} className="w-full h-12 rounded-xl border border-slate-200 px-3 bg-white" data-testid="select-package-type">
                <option value="custom">Custom (Trip-Linked)</option>
                <option value="ready">Ready Package (Reusable Template)</option>
              </select>
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Hero Image</Label>
              <div className="space-y-2">
                <Input value={heroImageUrl} onChange={(e) => setHeroImageUrl(e.target.value)} className="h-12 rounded-xl" placeholder="Paste URL or upload below" data-testid="input-hero-image" />
                {/* Phase B2 FEATURE 5 — Hero image upload is now available
                    in BOTH create and edit modes. In create mode, the first
                    upload auto-saves a draft itinerary (POST /api/itineraries),
                    obtains the new id, uploads the hero to that id, then
                    navigates into edit mode so subsequent saves PUT against
                    the same id (no duplicate-create on Save). */}
                <label className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-xl border border-dashed border-slate-300 cursor-pointer hover:bg-slate-100 transition-colors text-sm text-slate-600" data-testid="hero-upload-label">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="hero-upload-input"
                    onChange={async (e) => {
                      const raw = e.target.files?.[0];
                      if (!raw) return;
                      // Compress first so the auto-create payload doesn't
                      // race with a 10 MB image upload behind it.
                      const file = await compressImageFile(raw, { maxSizeMB: 1.0, maxWidthOrHeight: 1920 });
                      try {
                        let targetId = id;
                        if (!targetId) {
                          // Create-mode: validate the bare minimum needed by
                          // backend ItineraryCreate (title is required).
                          if (!title?.trim()) {
                            toast.error('Please enter a Title before uploading the hero image.');
                            return;
                          }
                          // Build same payload shape as handleSubmit so the
                          // auto-saved draft is consistent with what Save would
                          // produce. Kept inline (not extracted) per Rule ⑧.
                          const draftData = {
                            title, tripId, destination,
                            duration: parseInt(duration) || 1,
                            description, days, hotels, inclusions, exclusions,
                            hotelDisclaimer, flightDetails, termsAndConditions,
                            paymentPolicy, notes, paymentDetails,
                            quoteBasis, pricingOptions, quoteNotes,
                            curatorName, curatorPhone, curatorEmail,
                            roomDetails, guestName, packageType,
                            heroImageUrl, heroCaption,
                            highlights: Array.isArray(highlights) ? highlights : [],
                            paymentLink: (paymentLink || '').trim(),
                            paymentAmount: paymentAmount === '' ? 0 : Number(paymentAmount) || 0,
                          };
                          const createRes = await axios.post(`${BACKEND_URL}/api/itineraries`, draftData);
                          targetId = createRes.data.id;
                          toast.success('Draft saved — uploading hero image…');
                        }
                        const formData = new FormData();
                        formData.append('file', file);
                        const res = await axios.post(`${BACKEND_URL}/api/itineraries/${targetId}/upload-hero`, formData);
                        setHeroImageUrl(res.data.heroImageUrl);
                        toast.success(file.size < raw.size
                          ? `Hero image uploaded (compressed ${Math.round((1 - file.size/raw.size) * 100)}%)`
                          : 'Hero image uploaded!');
                        // If we just created the draft, switch the URL into
                        // edit mode so the next Save updates rather than creates.
                        if (!id && targetId) navigate(`/itineraries/${targetId}`);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Upload failed');
                      }
                    }}
                  />
                  Upload image file
                </label>
                {!id && (
                  <p className="text-xs text-slate-500">
                    First upload auto-saves a draft and switches to edit mode.
                  </p>
                )}
                {heroImageUrl && (
                  <div className="relative w-full h-24 rounded-xl overflow-hidden border border-slate-200">
                    <img src={heroImageUrl.startsWith('/') ? `${BACKEND_URL}${heroImageUrl}` : heroImageUrl} alt="Hero preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                )}
              </div>
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Hero Caption (Optional)</Label>
              <Input value={heroCaption} onChange={(e) => setHeroCaption(e.target.value)} className="h-12 rounded-xl" placeholder="e.g., Where Paradise Meets Culture" data-testid="input-hero-caption" />
            </div>
            {/* Phase B2 FEATURE 2 — Trip Highlights tag-input */}
            <div className="md:col-span-2">
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                Trip Highlights (Optional)
              </Label>
              <Input
                value={highlightDraft}
                onChange={(e) => setHighlightDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    const v = highlightDraft.trim();
                    if (v && !highlights.includes(v)) {
                      setHighlights([...highlights, v]);
                    }
                    setHighlightDraft('');
                  } else if (e.key === 'Backspace' && !highlightDraft && highlights.length) {
                    setHighlights(highlights.slice(0, -1));
                  }
                }}
                className="h-12 rounded-xl"
                placeholder="Type a highlight, press Enter (e.g., Pangong Lake sunrise)"
                data-testid="input-highlight-draft"
              />
              {highlights.length > 0 && (
                <div
                  className="flex flex-wrap gap-2 mt-3"
                  data-testid="highlights-chip-list"
                >
                  {highlights.map((h, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 text-sm text-blue-800"
                      data-testid={`highlight-chip-${i}`}
                    >
                      ✦ {h}
                      <button
                        type="button"
                        onClick={() => setHighlights(highlights.filter((_, j) => j !== i))}
                        className="ml-1 text-blue-400 hover:text-blue-700 font-bold"
                        aria-label={`Remove ${h}`}
                        data-testid={`highlight-chip-remove-${i}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            {/* Phase B2 FEATURE 4 — Payment link + amount (optional) */}
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                Payment Link (Optional)
              </Label>
              <Input
                type="url"
                value={paymentLink}
                onChange={(e) => setPaymentLink(e.target.value)}
                className="h-12 rounded-xl"
                placeholder="https://rzp.io/i/..."
                data-testid="input-payment-link"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                Payment Amount (₹, Optional)
              </Label>
              <Input
                type="number"
                min="0"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                className="h-12 rounded-xl"
                placeholder="e.g. 289922"
                data-testid="input-payment-amount"
              />
            </div>
          </div>
        </Card>

        {/* Quote Details & Curator */}
        <Card className="p-6 sm:p-8 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-6 flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary-700" /> Quote & Pricing Details
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Guest Name</Label>
              <Input value={guestName} onChange={(e) => setGuestName(e.target.value)} className="h-12 rounded-xl" placeholder="e.g., Samridhi" data-testid="input-guest-name" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Quote Basis</Label>
              <select value={quoteBasis} onChange={(e) => setQuoteBasis(e.target.value)} className="w-full h-12 rounded-xl border border-slate-200 px-3 bg-white" data-testid="select-quote-basis">
                <option value="Per Person">Per Person</option>
                <option value="Per Couple">Per Couple</option>
                <option value="Per Group">Per Group</option>
                <option value="Per Family">Per Family</option>
                <option value="Lumpsum">Lumpsum</option>
              </select>
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Room Details</Label>
              <Input value={roomDetails} onChange={(e) => setRoomDetails(e.target.value)} className="h-12 rounded-xl" placeholder="e.g., 1 Room" data-testid="input-room-details" />
            </div>
          </div>

          {/* PRICING — base per-pax inputs (feed the client breakup rows) */}
          <PricingSubLabel>Pricing</PricingSubLabel>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Flight price per person (₹)</Label>
              <input
                type="number" min="0"
                value={baseFlightPricePerPax ?? ''}
                onChange={(e) => setBaseFlightPricePerPax(e.target.value === '' ? null : (Number(e.target.value) || 0))}
                className={BORDERLESS_INPUT}
                placeholder="0"
                data-testid="input-base-flight-price"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Tours &amp; transfers per person (₹)</Label>
              <input
                type="number" min="0"
                value={baseToursPricePerPax ?? ''}
                onChange={(e) => setBaseToursPricePerPax(e.target.value === '' ? null : (Number(e.target.value) || 0))}
                className={BORDERLESS_INPUT}
                placeholder="0"
                data-testid="input-base-tours-price"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Advance required (%)</Label>
              <input
                type="number" min="0" max="100"
                value={advancePercentage != null ? Math.round(advancePercentage * 100) : ''}
                onChange={(e) => setAdvancePercentage(e.target.value === '' ? null : ((Number(e.target.value) || 0) / 100))}
                className={BORDERLESS_INPUT}
                placeholder="25"
                data-testid="input-advance-percentage"
              />
            </div>
          </div>

          {/* Pricing (1b — single price, no more Option 1/2 compare) */}
          <div className="mb-6">
            <Label className="text-sm font-semibold text-slate-700 mb-3 block">Pricing</Label>
            {(() => {
              const opt = pricingOptions[0] || { title: 'Package', items: [{ basis: '', price: '', note: '' }] };
              const updateOpt = (patch) => setPricingOptions([{ ...opt, ...patch }]);
              return (
                <div className="bg-slate-50 rounded-xl p-4 mb-3 border border-slate-200">
                  <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                    <Input
                      value={opt.title || ''}
                      onChange={(e) => updateOpt({ title: e.target.value })}
                      className="h-10 rounded-lg font-semibold max-w-xs"
                      placeholder="Package title"
                      data-testid="pricing-title"
                    />
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 whitespace-nowrap">This price is for:</span>
                      <select
                        value={opt.price_basis || 'per_person'}
                        onChange={(e) => updateOpt({ price_basis: e.target.value })}
                        className="h-9 rounded-lg border border-slate-200 px-2 text-sm bg-white"
                        data-testid="price-basis"
                      >
                        <option value="per_person">Per person</option>
                        <option value="per_couple">Per couple</option>
                        <option value="total_package">Total package</option>
                      </select>
                    </div>
                  </div>
                  {(opt.items || []).map((item, itemIdx) => (
                    <div key={`pline-${itemIdx}`} className="flex gap-2 mb-2">
                      <Input
                        value={item.basis}
                        onChange={(e) => {
                          const items = [...(opt.items || [])];
                          items[itemIdx] = { ...item, basis: e.target.value };
                          updateOpt({ items });
                        }}
                        className="h-9 rounded-lg flex-1"
                        placeholder="e.g., On Sharing Basis"
                        data-testid={`pricing-sharing-basis-${itemIdx}`}
                      />
                      <Input
                        value={item.price}
                        onChange={(e) => {
                          const items = [...(opt.items || [])];
                          items[itemIdx] = { ...item, price: e.target.value };
                          updateOpt({ items });
                        }}
                        className="h-9 rounded-lg flex-1"
                        placeholder="e.g., EUR 725 Per Person"
                      />
                      <Input
                        value={item.note}
                        onChange={(e) => {
                          const items = [...(opt.items || [])];
                          items[itemIdx] = { ...item, note: e.target.value };
                          updateOpt({ items });
                        }}
                        className="h-9 rounded-lg flex-1"
                        placeholder="e.g., + GST & TCS"
                      />
                      <Button type="button" variant="ghost" size="sm" onClick={() => {
                        const items = (opt.items || []).filter((_, i) => i !== itemIdx);
                        updateOpt({ items });
                      }} className="text-red-400 px-2">x</Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => {
                    updateOpt({ items: [...(opt.items || []), { basis: '', price: '', note: '' }] });
                  }} className="text-xs mt-1">+ Add Pricing Line</Button>
                  {/* Book Now — amount the client pays to confirm the booking. */}
                  <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="booking-amount-row">
                    <span className="text-xs text-slate-500 whitespace-nowrap">Book Now collects:</span>
                    <select
                      value={opt.booking_amount_type || 'advance'}
                      onChange={(e) => updateOpt({ booking_amount_type: e.target.value })}
                      className="h-9 rounded-lg border border-slate-200 px-2 text-sm bg-white"
                      data-testid="booking-amount-type"
                    >
                      <option value="advance">Advance ({advancePercentage != null ? Math.round(advancePercentage * 100) : 25}%)</option>
                      <option value="full">Full amount</option>
                      <option value="custom">Custom &#8377;</option>
                    </select>
                    {(opt.booking_amount_type === 'custom') && (
                      <Input
                        type="number" min="0"
                        value={opt.booking_custom_amount ?? ''}
                        onChange={(e) => updateOpt({ booking_custom_amount: e.target.value === '' ? '' : (Number(e.target.value) || 0) })}
                        className="h-9 rounded-lg w-36"
                        placeholder={'Amount in \u20B9'}
                        data-testid="booking-custom-amount"
                      />
                    )}
                  </div>
                  <div className="mt-3 text-xs text-slate-500" data-testid="couple-preview">
                    Couple mode preview: <span className="font-semibold text-slate-700">{fmtINRBuilder(optionCoupleTotal(opt, (showDiscount && discountPercentage !== '') ? Number(discountPercentage) : null))}</span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Discount (Correction 1) — optional, agent-controlled */}
          <div className="mb-6">
            {!showDiscount ? (
              <button type="button" onClick={() => setShowDiscount(true)} className="text-sm text-primary-700 font-medium hover:underline" data-testid="add-discount-link">
                + Add discount
              </button>
            ) : (
              <div className="flex flex-wrap items-end gap-6" data-testid="discount-fields">
                <div className="w-32">
                  <Label className="text-xs text-slate-500 mb-1 block">Discount (%)</Label>
                  <input
                    type="number" min="0" max="100"
                    value={discountPercentage}
                    onChange={(e) => setDiscountPercentage(e.target.value)}
                    className="w-full bg-transparent border-0 border-b border-slate-300 focus:border-primary-500 focus:outline-none px-0 py-1 text-slate-900"
                    placeholder="10"
                    data-testid="input-discount-percentage"
                  />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs text-slate-500 mb-1 block">Discount label</Label>
                  <input
                    value={discountLabel}
                    onChange={(e) => setDiscountLabel(e.target.value)}
                    className="w-full bg-transparent border-0 border-b border-slate-300 focus:border-primary-500 focus:outline-none px-0 py-1 text-slate-900"
                    placeholder="e.g., Early-bird discount"
                    data-testid="input-discount-label"
                  />
                </div>
                <button type="button" onClick={() => { setShowDiscount(false); setDiscountPercentage(''); setDiscountLabel(''); }} className="text-xs text-red-400 hover:underline pb-1" data-testid="remove-discount-link">
                  Remove
                </button>
              </div>
            )}
          </div>

          {/* ── Client view ── */}
          <PricingSubLabel>Client view</PricingSubLabel>
          <div className="space-y-5 mb-2">
            <div>
              <Label className="text-xs text-slate-500 mb-2 block">Visible pricing tabs</Label>
              <PaxTabsChecklist value={visiblePaxModes} onToggle={togglePaxMode} />
            </div>
            {(visiblePaxModes.includes('family') || visiblePaxModes.includes('group')) && (
              <div className="flex items-center justify-between max-w-md" data-testid="family-group-override-row">
                <div>
                  <span className="text-sm text-slate-700">Show Family/Group tabs anyway</span>
                  <p className="text-xs text-slate-400">Expose them even when the trip has fewer travellers.</p>
                </div>
                <Switch checked={showFamilyGroupTabs} onCheckedChange={setShowFamilyGroupTabs} data-testid="toggle-family-group-override" />
              </div>
            )}
          </div>

          {/* ── Agent margin (not visible to client) ── */}
          <PricingSubLabel>Agent margin (not visible to client)</PricingSubLabel>
          <div className="space-y-5 mb-2" data-testid="agent-margin-section">
            <div className="flex items-center justify-between max-w-md">
              <div>
                <span className="text-sm text-slate-700">International tour</span>
                <p className="text-xs text-slate-400">Applies 2% TCS to the client total.</p>
              </div>
              <Switch checked={isInternational} onCheckedChange={setIsInternational} data-testid="toggle-international" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-2 block">GST model</Label>
              <RadioGroup value={gstModel || 'none'} onValueChange={setGstModel} className="flex flex-wrap gap-6" data-testid="gst-model-group">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="none" id="gst-none" data-testid="gst-none" />
                  <Label htmlFor="gst-none" className="text-sm text-slate-700 cursor-pointer font-normal">None</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="package" id="gst-package" data-testid="gst-package" />
                  <Label htmlFor="gst-package" className="text-sm text-slate-700 cursor-pointer font-normal">5% on package</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="service_fee" id="gst-service" data-testid="gst-service" />
                  <Label htmlFor="gst-service" className="text-sm text-slate-700 cursor-pointer font-normal">18% on service fee</Label>
                </div>
              </RadioGroup>
            </div>
            {gstModel === 'service_fee' && (
              <div className="w-40">
                <Label className="text-xs text-slate-500 mb-1 block">Service fee (₹)</Label>
                <input
                  type="number" min="0"
                  value={serviceFee}
                  onChange={(e) => setServiceFee(e.target.value)}
                  className={BORDERLESS_INPUT}
                  placeholder="5000"
                  data-testid="input-service-fee"
                />
              </div>
            )}
          </div>

          {/* Quote Notes */}
          <div className="mb-6">
            <Label className="text-sm font-semibold text-slate-700 mb-3 block">Quote Notes (bullet points)</Label>
            {quoteNotes.map((note, idx) => (
              <div key={`qnote-${idx}`} className="flex gap-2 mb-2">
                <Input 
                  value={note} 
                  onChange={(e) => {
                    const updated = [...quoteNotes];
                    updated[idx] = e.target.value;
                    setQuoteNotes(updated);
                  }}
                  className="h-9 rounded-lg flex-1" 
                  placeholder="e.g., 12 year Child is considered as an Adult"
                />
                <Button type="button" variant="ghost" size="sm" onClick={() => setQuoteNotes(prev => prev.filter((_, i) => i !== idx))} className="text-red-400 px-2">x</Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setQuoteNotes(prev => [...prev, ''])} className="w-full rounded-xl mt-1" data-testid="add-quote-note-btn">
              <Plus className="h-4 w-4 mr-2" />Add Note
            </Button>
          </div>

          {/* Curator Info */}
          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-3 block">Curator / Sales Person</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input value={curatorName} onChange={(e) => setCuratorName(e.target.value)} className="h-12 rounded-xl" placeholder="Curator Name" data-testid="input-curator-name" />
              <Input value={curatorPhone} onChange={(e) => setCuratorPhone(e.target.value)} className="h-12 rounded-xl" placeholder="+91 98XXXXXXXX" data-testid="input-curator-phone" />
              <Input value={curatorEmail} onChange={(e) => setCuratorEmail(e.target.value)} className="h-12 rounded-xl" placeholder="email@travcations.com" data-testid="input-curator-email" />
            </div>
          </div>
        </Card>

        {/* 1. Hotel Details (Multiple) */}
        <Card className="p-6 sm:p-8 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Hotel className="h-5 w-5 text-primary-700" /> 1. Hotel Details
          </h2>
          <p className="text-sm text-slate-500 mb-6">Add multiple hotels for different locations/stays in this itinerary</p>
          <HotelEditor
            hotels={hotels}
            onUpdate={setHotels}
            onImageUpload={handleHotelImageUpload}
            destinationPhotoOverrides={destinationPhotoOverrides}
            onDestinationPhotoUpload={handleDestinationPhotoUpload}
            onDestinationPhotoClear={handleDestinationPhotoClear}
          />
          
          <div className="mt-6">
            <Label className="text-sm font-semibold text-slate-700 mb-2 block">Hotel Disclaimer</Label>
            <RichTextEditor value={hotelDisclaimer} onChange={setHotelDisclaimer} placeholder="e.g., Subject to availability, early check-in charges may apply..." minHeight={120} testId="rte-hotel-disclaimer" />
          </div>

          {/* Correction 3 — cruise inc/exc editor (only when a cruise stay exists) */}
          {hasCruise && (
            <div className="mt-8 pt-6 border-t border-slate-100" data-testid="cruise-inc-exc-editor">
              <h3 className="text-sm font-bold text-slate-800 mb-1 flex items-center gap-2">
                <Ship className="h-4 w-4 text-cyan-600" /> Cruise Package — Inclusions & Exclusions
              </h3>
              <p className="text-xs text-slate-500 mb-4">Shown as a separate card on the itinerary because this trip includes a cruise stay.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-xs font-semibold text-emerald-700 mb-2 block">Cruise Inclusions</Label>
                  {cruiseInclusions.map((inc, i) => (
                    <div key={`cinc-${i}`} className="flex gap-2 mb-2">
                      <Input value={inc} onChange={(e) => { const u = [...cruiseInclusions]; u[i] = e.target.value; setCruiseInclusions(u); }} className="h-9 rounded-lg flex-1 text-sm" data-testid={`cruise-inclusion-${i}`} />
                      <Button type="button" variant="ghost" size="sm" onClick={() => setCruiseInclusions(prev => prev.filter((_, x) => x !== i))} className="text-red-400 px-2">x</Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => setCruiseInclusions(prev => [...prev, ''])} className="text-xs mt-1" data-testid="add-cruise-inclusion-btn"><Plus className="h-3 w-3 mr-1" />Add inclusion</Button>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-rose-700 mb-2 block">Cruise Exclusions</Label>
                  {cruiseExclusions.map((exc, i) => (
                    <div key={`cexc-${i}`} className="flex gap-2 mb-2">
                      <Input value={exc} onChange={(e) => { const u = [...cruiseExclusions]; u[i] = e.target.value; setCruiseExclusions(u); }} className="h-9 rounded-lg flex-1 text-sm" data-testid={`cruise-exclusion-${i}`} />
                      <Button type="button" variant="ghost" size="sm" onClick={() => setCruiseExclusions(prev => prev.filter((_, x) => x !== i))} className="text-red-400 px-2">x</Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => setCruiseExclusions(prev => [...prev, ''])} className="text-xs mt-1" data-testid="add-cruise-exclusion-btn"><Plus className="h-3 w-3 mr-1" />Add exclusion</Button>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* 2. Day-wise Itinerary */}
        <Card className="p-6 sm:p-8 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-heading font-bold text-slate-900 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary-700" /> 2. Day-wise Itinerary
            </h2>
            <span className="text-sm text-slate-500 bg-slate-100 px-3 py-1 rounded-full">{days.length} Days</span>
          </div>

          <div className="space-y-4">
            {days.map((day, idx) => (
              <DayEditor
                key={`day-${idx}`}
                day={day}
                dayIndex={idx}
                onUpdate={updateDay}
                onRemove={removeDay}
                onImageUpload={handleImageUpload}
                canRemove={days.length > 1}
              />
            ))}
          </div>
          
          <Button type="button" onClick={() => handleDurationChange(days.length + 1)} className="mt-4 bg-primary-700 text-white rounded-xl w-full" data-testid="add-day-btn">
            <Plus className="h-4 w-4 mr-2" />Add Another Day
          </Button>
        </Card>

        {/* 3. Inclusions & Exclusions */}
        <Card className="p-6 sm:p-8 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
          <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
            <h2 className="text-xl font-heading font-bold text-slate-900">3. Inclusions & Exclusions</h2>
            <Select value="" onValueChange={(idVal) => requestApplyTemplate(destTemplates.find((t) => t.id === idVal))}>
              <SelectTrigger className="h-9 w-[240px]" data-testid="apply-dest-template-select">
                <SelectValue placeholder="Apply destination template…" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {destTemplateOptions}
              </SelectContent>
            </Select>
          </div>

          {showDestChip && (
            <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5" data-testid="dest-template-chip">
              <p className="text-sm text-amber-800">
                Template available for <strong>{matchedTemplate.destination_name}</strong> — apply its inclusions, exclusions &amp; T&amp;C?
              </p>
              <div className="flex items-center gap-2 shrink-0">
                <Button type="button" size="sm" onClick={() => requestApplyTemplate(matchedTemplate)} className="bg-amber-600 hover:bg-amber-700 text-white h-8" data-testid="apply-dest-template-chip-btn">Apply</Button>
                <button type="button" onClick={() => setDismissedTemplateId(matchedTemplate.id)} className="text-amber-500 hover:text-amber-700" data-testid="dismiss-dest-template-chip" aria-label="Dismiss"><X className="h-4 w-4" /></button>
              </div>
            </div>
          )}

          <TemplatesSection
            templates={templates}
            selectedInclusions={inclusions}
            selectedExclusions={exclusions}
            onToggleInclusion={toggleInclusion}
            onToggleExclusion={toggleExclusion}
            onAddTemplate={addTemplate}
            onEditTemplate={editTemplate}
            onDeleteTemplate={deleteTemplate}
            onReorderInclusions={setInclusions}
            onReorderExclusions={setExclusions}
          />

          <AlertDialog open={Boolean(applyConfirm)} onOpenChange={(o) => !o && setApplyConfirm(null)}>
            <AlertDialogContent data-testid="apply-template-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Replace current inclusions &amp; exclusions?</AlertDialogTitle>
                <AlertDialogDescription>
                  Applying the &quot;{applyConfirm?.destination_name}&quot; template will replace the current inclusions, exclusions and T&amp;C. You can still edit everything afterwards.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="apply-template-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => { doApplyTemplate(applyConfirm); setApplyConfirm(null); }} className="bg-teal-600 hover:bg-teal-700" data-testid="apply-template-confirm">Apply template</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </Card>

        {/* 4. Flight Details — structured (from flight voucher) vs manual textarea */}
        {(() => {
          const anyVoucherHasStructured = flightVouchers.some(
            (v) => (v.flight?.segments?.length || 0) > 0,
          );
          const defaultMode = anyVoucherHasStructured ? 'structured' : 'manual';
          const effectiveMode = flightSectionMode || defaultMode;
          const selectedVoucher = flightVouchers.length === 1
            ? flightVouchers[0]
            : flightVouchers.find((v) => v.voucherId === selectedFlightVoucherId);
          const hasStructured = (selectedVoucher?.flight?.segments?.length || 0) > 0;
          const toggleFlightMode = () => {
            if (effectiveMode === 'structured') setFlightSectionMode('manual');
            else if (anyVoucherHasStructured) setFlightSectionMode('structured');
          };
          return (
            <Card className="p-6 sm:p-8 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-heading font-bold text-slate-900 flex items-center gap-2">
                  <Plane className="h-5 w-5 text-primary-700" /> 4. Flight Details
                </h2>
                {anyVoucherHasStructured && (
                  <button
                    type="button"
                    onClick={toggleFlightMode}
                    className="text-[11px] text-slate-500 cursor-pointer hover:underline bg-transparent border-0 p-0"
                    data-testid="flight-mode-toggle-btn"
                  >
                    {effectiveMode === 'structured' ? 'Switch to manual entry ↗' : 'Switch to structured view ↗'}
                  </button>
                )}
              </div>

              {effectiveMode === 'structured' ? (
                <>
                  {flightVouchers.length > 1 && (
                    <div className="mb-4">
                      <Label className="text-xs text-slate-500 mb-1.5 block">Flight voucher</Label>
                      <select
                        value={selectedFlightVoucherId}
                        onChange={(e) => setSelectedFlightVoucherId(e.target.value)}
                        data-testid="flight-voucher-select"
                        className="w-full max-w-md p-2 border border-slate-200 rounded-lg text-sm bg-white"
                      >
                        <option value="">— Select a flight voucher —</option>
                        {flightVouchers.map((v) => {
                          const f = v.flight || {};
                          const segs = Array.isArray(f.segments) ? f.segments : [];
                          const first = segs[0] || {};
                          const last = segs[segs.length - 1] || first;
                          const route = [
                            first.departureAirportCode || first.departureCity,
                            last.arrivalAirportCode || last.arrivalCity,
                          ].filter(Boolean).join(' → ');
                          const dateStr = first.departureDate || '';
                          const ref = v.trvRef || v.voucherId;
                          const tag = (segs.length || 0) > 0 ? '' : ' [legacy]';
                          return (
                            <option key={v.voucherId} value={v.voucherId}>
                              {`${ref}${route ? ` — ${route}` : ''}${dateStr ? ` (${dateStr})` : ''}${tag}`}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  )}
                  {selectedVoucher && hasStructured ? (
                    <ItineraryFlightCard
                      flight={selectedVoucher.flight}
                      company={companyDoc}
                      passengers={selectedVoucher.flight?.passengers || []}
                    />
                  ) : flightVouchers.length > 1 && !selectedFlightVoucherId ? (
                    <div
                      className="text-sm text-slate-500 italic"
                      data-testid="flight-structured-empty-prompt"
                    >
                      Select a flight voucher above to view structured details.
                    </div>
                  ) : selectedVoucher ? (
                    <div
                      className="text-sm text-slate-500 italic"
                      data-testid="flight-structured-legacy-notice"
                    >
                      This voucher uses the legacy schema — no structured view available.
                    </div>
                  ) : null}
                </>
              ) : (
                <Textarea
                  value={flightDetails}
                  onChange={(e) => setFlightDetails(e.target.value)}
                  rows={4}
                  className="rounded-xl bg-blue-50"
                  placeholder="Flight details (airline, times, PNR)..."
                  data-testid="flight-details-textarea"
                />
              )}
            </Card>
          );
        })()}

        {/* 5. Terms & Conditions */}
        <Card className="p-6 sm:p-8 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
            <ScrollText className="h-5 w-5 text-primary-700" /> 5. Terms & Conditions
          </h2>
          <RichTextEditor value={termsAndConditions} onChange={setTermsAndConditions} placeholder="Terms and conditions…" minHeight={140} testId="rte-terms" />
        </Card>

        {/* 6. Payment & Cancellation Policy */}
        <Card className="p-6 sm:p-8 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary-700" /> 6. Payment & Cancellation Policy
          </h2>
          <RichTextEditor value={paymentPolicy} onChange={setPaymentPolicy} placeholder="Payment terms and cancellation policy…" minHeight={140} testId="rte-policy" />
        </Card>

        {/* 7. Notes */}
        <Card className="p-6 sm:p-8 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-4">7. Notes</h2>
          <RichTextEditor value={notes} onChange={setNotes} placeholder="Additional notes…" minHeight={120} testId="rte-notes" />
        </Card>

        {/* 8. Payment Details */}
        <Card className="p-6 sm:p-8 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-4">8. Payment Details & Links</h2>
          <Textarea value={paymentDetails} onChange={(e) => setPaymentDetails(e.target.value)} rows={4} className="rounded-xl bg-green-50" placeholder="Bank details and/or payment links with validity..." />
        </Card>

        {/* 9. Supplement Costs (optional add-ons) */}
        <Card className="p-6 sm:p-8 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-2 flex items-center gap-2">
            <Plus className="h-5 w-5 text-primary-700" /> 9. Supplement Costs
          </h2>
          <p className="text-sm text-slate-500 mb-6">Optional add-ons (e.g., water sports, candle-light dinner) shown to the client before the payment section.</p>
          <SupplementsEditor value={supplementCosts} onChange={setSupplementCosts} />
        </Card>

        {/* 10. Voucher (booking-time travel documents) */}
        <Card className="p-6 sm:p-8 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl" data-testid="voucher-section-card">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
            <h2 className="text-xl font-heading font-bold text-slate-900 flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary-700" /> 10. Voucher
            </h2>
            {id && (
              <div className="flex items-center gap-2">
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={handlePreviewVoucher}
                  disabled={!!voucherBusy}
                  className="rounded-xl"
                  data-testid="preview-voucher-btn"
                >
                  {voucherBusy === 'preview' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />}
                  Preview Voucher
                </Button>
                <Button
                  type="button" size="sm"
                  onClick={handleSendVoucherWhatsApp}
                  disabled={!!voucherBusy}
                  className="rounded-xl bg-[#25D366] hover:bg-[#1ebe5b] text-white"
                  data-testid="send-voucher-whatsapp-btn"
                >
                  {voucherBusy === 'whatsapp' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <MessageCircle className="h-4 w-4 mr-1.5" />}
                  Send on WhatsApp
                </Button>
                <Button
                  type="button" size="sm"
                  onClick={handleDownloadVoucher}
                  disabled={!!voucherBusy}
                  className="rounded-xl bg-gradient-to-br from-primary-700 to-primary-600 text-white"
                  data-testid="download-voucher-btn"
                >
                  {voucherBusy === 'download' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
                  Download Voucher
                </Button>
              </div>
            )}
          </div>
          <p className="text-sm text-slate-500 mb-6">Confirmed booking details — coordinator, per-day activities &amp; vehicles, accommodation confirmations, things to carry, transport, recommended places &amp; local currency for the traveller's voucher. <span className="text-slate-400">This is the trip's main itinerary voucher — Preview or Download saves your edits first.</span></p>
          <VoucherTab
            destination={destination}
            voucherStatus={voucherStatus} setVoucherStatus={setVoucherStatus}
            bookingRef={bookingRef} setBookingRef={setBookingRef}
            coordinator={coordinator} setCoordinator={setCoordinator}
            emergencyContacts={emergencyContacts} setEmergencyContacts={setEmergencyContacts}
            days={days} setDays={setDays}
            hotels={hotels} setHotels={setHotels}
            thingsToCarry={thingsToCarry} setThingsToCarry={setThingsToCarry}
            transportOptions={transportOptions} setTransportOptions={setTransportOptions}
            recommendedPlaces={recommendedPlaces} setRecommendedPlaces={setRecommendedPlaces}
            localCurrency={localCurrency} setLocalCurrency={setLocalCurrency}
            voucherNotes={voucherNotes} setVoucherNotes={setVoucherNotes}
          />
        </Card>

        {/* Submit (desktop) — hidden on mobile where the sticky save bar handles it. */}
        <div className="hidden md:flex justify-end items-center gap-4 pt-4">
          {id && autoSavedAt && (
            <span className="text-xs text-slate-400" data-testid="autosave-indicator">
              Auto-saved at {autoSavedAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="px-8 h-12 rounded-xl">Cancel</Button>
          <Button type="submit" disabled={saving} className="bg-gradient-to-br from-primary-700 to-primary-600 text-white shadow-lg px-10 h-12 rounded-xl font-semibold" data-testid="save-itinerary-btn">
            <Save className="h-5 w-5 mr-2" />
            {saving ? 'Saving...' : (id ? 'Update Itinerary' : 'Create Itinerary')}
          </Button>
        </div>

        {/* Spacer so the sticky mobile save bar never hides content. */}
        <div className="md:hidden h-20" aria-hidden="true" />

        {/* Sticky mobile save bar — sits above the bottom nav. */}
        <div
          className="md:hidden fixed inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-2.5"
          style={{ bottom: 'calc(56px + env(safe-area-inset-bottom))' }}
          data-testid="builder-sticky-save"
        >
          <Button type="submit" disabled={saving} className="w-full h-12 bg-gradient-to-br from-primary-700 to-primary-600 text-white rounded-xl font-semibold" data-testid="save-itinerary-btn-mobile">
            <Save className="h-5 w-5 mr-2" />
            {saving ? 'Saving...' : (id ? 'Update Itinerary' : 'Create Itinerary')}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default ItineraryBuilder;
